// ADR-199: Reports → Integrity Checks — pure helpers (unit-tested).
// Samples are raw rows from each check's SQL, so their columns differ per check
// and are rendered generically. Values are shown as received: date-only strings
// stay strings (ADR-197), nothing is parsed into a Date.
import { CHECK_STATUSES } from '../constants/integrityChecks';

const ACRONYMS = { id: 'ID', bu: 'BU', ids: 'IDs', '24h': '24 h' };

/** 'employees_over_max_open_episodes' → 'Employees over max open episodes'. */
export const humanizeKey = (key) => {
  if (!key) return '';
  const words = String(key).split(/[_\s]+/).filter(Boolean)
    .map((w) => ACRONYMS[w.toLowerCase()] || w.toLowerCase());
  if (words.length === 0) return '';
  const [first, ...rest] = words;
  return [first === first.toUpperCase() ? first : first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(' ');
};

export const checkStatus = (result) => {
  if ((result?.count || 0) > 0) return CHECK_STATUSES.VIOLATIONS;
  if ((result?.informationalCount || 0) > 0) return CHECK_STATUSES.INFO_ONLY;
  return CHECK_STATUSES.PASSED;
};

const STATUS_ORDER = { VIOLATIONS: 0, INFO_ONLY: 1, PASSED: 2 };

/** Checks with violations first (largest first), then informational, then passing; stable otherwise. */
export const sortResults = (results = []) => results
  .map((r, i) => ({ r, i }))
  .sort((a, b) => {
    const s = STATUS_ORDER[checkStatus(a.r)] - STATUS_ORDER[checkStatus(b.r)];
    if (s !== 0) return s;
    const c = (b.r.count || 0) - (a.r.count || 0);
    return c !== 0 ? c : a.i - b.i;
  })
  .map(({ r }) => r);

/** Union of the sample rows' keys, in first-seen order. */
export const sampleColumns = (samples = []) => {
  const seen = [];
  samples.forEach((row) => {
    Object.keys(row || {}).forEach((key) => {
      if (!seen.includes(key)) seen.push(key);
    });
  });
  return seen;
};

const MAX_CELL = 120;

/** { text, full } for one cell; `full` is set when text was shortened. */
export const formatCell = (value) => {
  if (value === null || value === undefined || value === '') return { text: '—', full: null };
  if (typeof value === 'boolean') return { text: value ? 'Yes' : 'No', full: null };
  const raw = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return raw.length > MAX_CELL ? { text: `${raw.slice(0, MAX_CELL - 1)}…`, full: raw } : { text: raw, full: null };
};

/** Totals across the scopes that loaded. */
export const summarizeScopes = (reports = {}) => {
  const loaded = Object.values(reports).filter(Boolean);
  const results = loaded.flatMap((r) => r.results || []);
  return {
    loadedScopes: loaded.length,
    totalViolations: loaded.reduce((sum, r) => sum + (r.totalViolations || 0), 0),
    failingChecks: results.filter((r) => (r.count || 0) > 0).length,
    informational: results.reduce((sum, r) => sum + (r.informationalCount || 0), 0),
    totalChecks: results.length,
    healthy: loaded.length > 0 && loaded.every((r) => r.healthy),
  };
};

/** "Default 1 · Romania 2 · USA 1" from employment's openContractLimits. */
export const describeContractLimits = (limits) => {
  if (!limits) return '';
  const parts = [];
  if (limits.default !== undefined && limits.default !== null) parts.push(`Default ${limits.default}`);
  (limits.regions || []).forEach((r) => {
    if (r?.name && r.effective !== undefined && r.effective !== null) parts.push(`${r.name} ${r.effective}`);
  });
  return parts.join(' · ');
};
